import {
  fetchBoardCoreStatus,
  installBoardCoresViaHelper,
  resolveHelperConnectionState,
  fetchHelperHealth,
} from '@/features/serial/helperClient';
import { showHelperOfflineDialog } from '@/features/helper/helperOfflineDialog';
import { getHelperDownloadUrl } from '@/config/helperDownload';
import { useArduinoSetupDialogStore } from '@/state/arduinoSetupDialogStore';
import { useSerialStore } from '@/state/serialStore';
import { getAllBoards } from '@skyrover/board-definitions';
import { ARDUINO_CORES } from '@skyrover/shared/constants';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface BoardManagerDialogProps {
  open: boolean;
  onClose: () => void;
}

interface CoreRowState {
  installing: boolean;
  log: string;
  error: string | null;
  progress: number;
  message: string;
}

export function BoardManagerDialog({ open, onClose }: BoardManagerDialogProps) {
  const refreshHelperStatus = useSerialStore((state) => state.refreshHelperStatus);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cliAvailable, setCliAvailable] = useState(false);
  const [installedByCore, setInstalledByCore] = useState<Record<string, { installed: boolean; version?: string }>>(
    {},
  );
  const [rowState, setRowState] = useState<Record<string, CoreRowState>>({});

  const boardGroups = useMemo(() => {
    const boards = getAllBoards();
    return ARDUINO_CORES.map((core) => ({
      core,
      boards: boards.filter((board) => board.compiler.platform === core.id),
    }));
  }, []);

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const health = await fetchHelperHealth();
      const connection = resolveHelperConnectionState(health);
      if (connection === 'offline') {
        setLoadError('Skyrover Helper is not running on this PC.');
        setCliAvailable(false);
        setInstalledByCore({});
        return;
      }

      const status = await fetchBoardCoreStatus();
      setCliAvailable(status.cliAvailable);
      setInstalledByCore(
        Object.fromEntries(
          status.cores.map((core) => [
            core.id,
            core.version !== undefined
              ? { installed: core.installed, version: core.version }
              : { installed: core.installed },
          ]),
        ),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not read board packages.';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    void refreshStatus();
  }, [open, refreshStatus]);

  const handleDownloadCore = async (coreId: string) => {
    setRowState((current) => ({
      ...current,
      [coreId]: {
        installing: true,
        log: '',
        error: null,
        progress: 5,
        message: 'Preparing download…',
      },
    }));

    try {
      const result = await installBoardCoresViaHelper([coreId], (event) => {
        setRowState((current) => {
          const row = current[coreId];
          if (!row?.installing) {
            return current;
          }

          if (event.type === 'progress') {
            return {
              ...current,
              [coreId]: {
                ...row,
                progress: event.percent ?? row.progress,
                message: event.message ?? row.message,
              },
            };
          }

          if (event.type === 'log' && event.log) {
            return {
              ...current,
              [coreId]: {
                ...row,
                log: row.log + event.log,
              },
            };
          }

          return current;
        });
      });

      setRowState((current) => ({
        ...current,
        [coreId]: {
          installing: false,
          log: result.log,
          error: result.success ? null : (result.error ?? 'Install failed.'),
          progress: result.success ? 100 : current[coreId]?.progress ?? 0,
          message: result.success ? 'Installation complete' : (result.error ?? 'Install failed.'),
        },
      }));

      if (result.success) {
        await refreshStatus();
        await refreshHelperStatus();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Install failed.';
      setRowState((current) => ({
        ...current,
        [coreId]: {
          installing: false,
          log: current[coreId]?.log ?? '',
          error: message,
          progress: 0,
          message: message,
        },
      }));
    }
  };

  const handleClose = () => {
    const anyInstalling = Object.values(rowState).some((row) => row.installing);
    if (anyInstalling) {
      return;
    }
    onClose();
    setRowState({});
    setLoadError(null);
  };

  const openHelperOrSetup = async () => {
    const health = await fetchHelperHealth();
    const connection = resolveHelperConnectionState(health);
    if (connection === 'offline' || loadError?.includes('v0.1.4')) {
      showHelperOfflineDialog();
      return;
    }
    if (connection === 'setup_required') {
      useArduinoSetupDialogStore.getState().show();
    }
  };

  const helperUpdateRequired = loadError?.includes('v0.1.4') ?? false;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        Board Manager
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh status">
          <span>
            <IconButton size="small" onClick={() => void refreshStatus()} disabled={loading} aria-label="Refresh">
              {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Download only the board packages you need. Arduino Uno, Nano, and Mega share one package; each
          ESP family has its own.
        </Typography>

        {loadError ? (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              helperUpdateRequired ? (
                <Button
                  component="a"
                  href={getHelperDownloadUrl()}
                  download
                  color="inherit"
                  size="small"
                >
                  Download helper
                </Button>
              ) : (
                <Button color="inherit" size="small" onClick={() => void openHelperOrSetup()}>
                  Fix
                </Button>
              )
            }
          >
            {loadError}
          </Alert>
        ) : null}

        {!loading && !cliAvailable && !loadError ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Arduino CLI is not installed yet. Downloading any board package will install it automatically first
            (small download).
          </Alert>
        ) : null}

        {loading && !loadError ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Checking Arduino CLI and installed board packages…
          </Alert>
        ) : null}

        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

        <Stack spacing={1.5}>
          {boardGroups.map(({ core, boards }) => {
            const status = installedByCore[core.id];
            const row = rowState[core.id];
            const isInstalling = row?.installing ?? false;

            return (
              <Box
                key={core.id}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {core.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Boards: {boards.map((board) => board.displayName).join(', ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Package: {core.id}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {status?.installed ? (
                      <Chip
                        size="small"
                        color="success"
                        icon={<CheckCircleIcon />}
                        label={status.version ? `Installed ${status.version}` : 'Installed'}
                      />
                    ) : (
                      <Chip size="small" label="Not installed" variant="outlined" />
                    )}

                    <Button
                      size="small"
                      variant={status?.installed ? 'outlined' : 'contained'}
                      startIcon={isInstalling ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                      disabled={isInstalling || Boolean(loadError)}
                      onClick={() => void handleDownloadCore(core.id)}
                    >
                      {isInstalling ? 'Installing…' : status?.installed ? 'Reinstall' : 'Download'}
                    </Button>
                  </Box>
                </Box>

                {isInstalling ? (
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {row?.message ?? 'Installing…'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row?.progress ?? 0}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={row?.progress ?? 0}
                      sx={{ mb: 1, height: 6, borderRadius: 1 }}
                    />
                  </Box>
                ) : null}

                {row?.error ? (
                  <Alert severity="error" sx={{ mt: 1.5 }}>
                    {row.error}
                  </Alert>
                ) : null}

                {row?.log ? (
                  <Box
                    component="pre"
                    sx={{
                      mt: 1.5,
                      mb: 0,
                      p: 1,
                      maxHeight: 120,
                      overflow: 'auto',
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      fontSize: 11,
                      fontFamily: 'Consolas, monospace',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {row.log}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
